import { useCallback, useEffect, useRef } from 'react';

type TimerStatus = 'RUNNING' | 'PAUSED' | 'STOPPED';

type TimerState = {
  status: TimerStatus;
  time: number;
};

type TimerConfig = {
  autostart?: boolean;
  endTime?: number;
  interval: number;
  onTimerEnd?: () => void;
  onTimerTick?: (time: number) => void;
};

type UseTimerReturnValue = {
  pause: () => void;
  reset: () => void;
  start: () => void;
  state: TimerState;
};

export function useTimer({
  autostart = true,
  endTime,
  interval,
  onTimerEnd,
  onTimerTick,
}: TimerConfig): UseTimerReturnValue {
  const state = useRef<TimerState>({ time: 0, status: autostart ? 'RUNNING' : 'STOPPED' });
  const setIntervalId = useRef<ReturnType<typeof setInterval>>();

  const onTimerTickRef = useRef<(time: number) => void>();
  onTimerTickRef.current = onTimerTick;

  const onTimerEndRef = useRef<() => void>();
  onTimerEndRef.current = onTimerEnd;

  const startTimer = useCallback(() => {
    state.current.time += interval;

    if (endTime !== undefined && state.current.time >= endTime) {
      state.current.status = 'STOPPED';

      onTimerEndRef.current?.();

      clearInterval(setIntervalId.current!);
    } else {
      onTimerTickRef.current?.(state.current.time);
    }
  }, [endTime, interval]);

  const pause = useCallback(() => {
    state.current.status = 'PAUSED';

    if (setIntervalId.current) clearInterval(setIntervalId.current);
  }, []);

  const reset = useCallback(() => {
    state.current.time = 0;
    state.current.status = 'STOPPED';

    if (setIntervalId.current) clearInterval(setIntervalId.current);
  }, []);

  const start = useCallback(() => {
    if (endTime !== undefined && state.current.time >= endTime) return;

    state.current.status = 'RUNNING';

    if (setIntervalId.current) clearInterval(setIntervalId.current);

    setIntervalId.current = setInterval(startTimer, interval);
  }, [interval, startTimer, endTime]);

  useEffect(() => {
    if (state.current.status === 'RUNNING') {
      setIntervalId.current = setInterval(startTimer, interval);
    }

    return () => {
      if (setIntervalId.current) clearInterval(setIntervalId.current);
    };
  }, [interval, startTimer]);

  return {
    state: state.current,
    start,
    pause,
    reset,
  };
}
